import * as React from 'react';
import { Fragment } from 'react';

/** Material UI Packages. */
import { Checkbox, Typography, Autocomplete, TextField, Popper } from '@mui/material';
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@mui/x-data-grid-pro';
import { styled } from '@mui/material/styles';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import { trucking_type } from 'shared/config/constantsData';
import { CustomAutocompleteCell_TradePartner, DateTypingEditCell } from 'shared/ui/RenderCell';
import CustomDetailPanelToggle from 'shared/ui/CustomDetailPanelToggle';
import { createQuickFilterFn } from 'shared/lib/quickFilterUtils';
import { parseDateToLocal } from 'shared/lib/dateFormatter';
import { parsePastedDate, createNameFilterOperators } from 'shared/lib/datagridHelpers';
import { TeamEditCell } from 'shared/ui/TeamCell';
import {
    actionsCol,
    progressCol,
    statusCol,
    renderHazmatCell,
    truckerCol,
    truckerLinesByShipment,
} from './CommonMainDef';

const StyledPopper = styled(Popper)({
    '& .MuiAutocomplete-listbox': {
        fontSize: '11px',
    },
});

const hasHazmat = (rows) => rows.some((item) => item.hazmat === true);

/** Define Trucking Datagrid Fields. */
export const getColumns = (
    shipmentList,
    bolShipmentList,
    cntrShipmentList,
    splitShipmentList,
    commentList,
    selectValue,
    statusList,
    tradePartnerOptions,
    locationOptions,
    vesselOptions,
    handleSelectionChange,
    handleEmailTemplateOpen,
    handleCommentOpen,
    handleHistoryOpen,
    handleTradePartnerChange,
    handleLocationChange,
    handleVesselChange,
    shipmentRow,
    setShipmentRow,
    todoSummary,
    tabValue,
) => [
    {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        minWidth: 25,
        width: 30,
        renderCell: (params) => <CustomDetailPanelToggle id={params.id} />,
    },
    {
        field: 'selection',
        headerName: '',
        minWidth: 25,
        width: 30,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: (params) => {
            if (params.rowNode.type === 'group') {
                return '';
            }
            return (
                <Checkbox
                    checked={params.id === selectValue || false}
                    onChange={() => handleSelectionChange(params.id)}
                    sx={customized_style.itemDatagrid_Typography}
                />
            );
        },
    },
    {
        field: 'id',
        headerName: 'ID',
        align: 'center',
        headerAlign: 'center',
        width: 30,
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
    },
    {
        field: 'il_team_id',
        headerName: 'Team',
        width: 100,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        // Team reads back as { id, name } like Client (OS-566); the grid shows, sorts, filters and searches the name.
        valueGetter: (params) => params,
        valueFormatter: (params) => params?.name ?? '',
        groupingValueGetter: (value) => value?.name || '',
        renderCell: (params) => (
            <Typography sx={customized_style.itemDatagrid_Typography}>{params.value?.name || ''}</Typography>
        ),
        renderEditCell: (params) => <TeamEditCell params={params} />,
        filterOperators: createNameFilterOperators(),
        getApplyQuickFilterFn: createQuickFilterFn,
    },
    {
        field: 'il_opus_reference_id',
        headerName: 'Opus Ref',
        align: 'center',
        headerAlign: 'center',
        width: 75,
        editable: true,
        headerClassName: 'custom-editable-header',
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
    },
    ...actionsCol({
        tabValue,
        commentList,
        handleEmailTemplateOpen,
        handleCommentOpen,
        handleHistoryOpen,
    }),
    {
        field: 'il_trade_partner_id_client',
        headerName: 'Client',
        width: 120,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            return params ? params?.name : '';
        },
        groupingValueGetter: (value) => {
            return value?.name || '';
        },
        renderCell: (params) => {
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {params.value?.name || params.value}
                </Typography>
            );
        },
        renderEditCell: (params) => {
            return (
                <CustomAutocompleteCell_TradePartner
                    params={params}
                    options={tradePartnerOptions}
                    handleInputChange={handleTradePartnerChange}
                />
            );
        },
        filterOperators: createNameFilterOperators(),
        getApplyQuickFilterFn: createQuickFilterFn,
    },
    {
        field: 'il_trade_partner_id_shipper',
        headerName: 'Shipper',
        width: 120,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-editable-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            return params ? params?.name : '';
        },
        groupingValueGetter: (value) => {
            return value?.name || '';
        },
        renderCell: (params) => {
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {params.value?.name || params.value}
                </Typography>
            );
        },
        renderEditCell: (params) => {
            return (
                <CustomAutocompleteCell_TradePartner
                    params={params}
                    options={tradePartnerOptions}
                    handleInputChange={handleTradePartnerChange}
                />
            );
        },
        filterOperators: createNameFilterOperators(),
        getApplyQuickFilterFn: createQuickFilterFn,
    },
    {
        field: 'il_trade_partner_id_consignee',
        headerName: 'Consignee',
        width: 120,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-editable-header',
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            return params ? params?.name : '';
        },
        groupingValueGetter: (value) => {
            return value?.name || '';
        },
        renderCell: (params) => {
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {params.value?.name || params.value}
                </Typography>
            );
        },
        renderEditCell: (params) => {
            return (
                <CustomAutocompleteCell_TradePartner
                    params={params}
                    options={tradePartnerOptions}
                    handleInputChange={handleTradePartnerChange}
                />
            );
        },
        filterOperators: createNameFilterOperators(),
        getApplyQuickFilterFn: createQuickFilterFn,
    },
    {
        field: 'bol',
        headerName: 'BOL',
        width: 120,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-editable-header',
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
    },
    {
        field: 'shipment_type',
        headerName: 'Type',
        width: 80,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-editable-header',
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
        renderEditCell: (params) => (
            <Autocomplete
                disableClearable
                disablePortal
                options={trucking_type}
                onChange={(event, newValue) => {
                    params.api.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: newValue,
                    });
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        variant="standard"
                        autoFocus
                        sx={{
                            '& .MuiInputBase-root': {
                                border: 'none',
                            },
                        }}
                    />
                )}
                value={shipmentList.filter((item) => item.id === params.id)[0].shipment_type || ''}
                slotProps={{
                    popper: {
                        component: StyledPopper,
                    },
                }}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    '& .MuiInputBase-input': {
                        fontSize: '11px',
                    },
                }}
            />
        ),
    },
    {
        field: 'alias',
        headerName: 'Alias',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        renderCell: (params) => {
            const matchedHBL = bolShipmentList.filter((bol) => bol.il_ftl_ltl_id === params.row.id);
            const result = matchedHBL.map((item) => item.alias).join('\n');
            return result ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },
    {
        field: 'quantity',
        headerName: 'Vol',
        width: 50,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        renderCell: (params) => {
            const matchedHBL = bolShipmentList.filter((bol) => bol.il_ftl_ltl_id === params.row.id);
            const result = matchedHBL.map((item) => item.quantity).join('\n');
            return result ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },
    {
        field: 'il_opus_volume_unit_id',
        headerName: 'Unit',
        width: 70,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        renderCell: (params) => {
            const matchedHBL = bolShipmentList.filter((bol) => bol.il_ftl_ltl_id === params.row.id);
            const result = matchedHBL.map((item) => item.il_opus_volume_unit_id).join('\n');
            return result ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },
    {
        field: 'haz',
        headerName: 'HAZ',
        width: 50,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        valueGetter: (value, row) => Boolean(row && hasHazmat(bolShipmentList.filter((bol) => bol.il_ftl_ltl_id === row.id))),
        type: 'boolean',
        renderCell: renderHazmatCell,
    },
    {
        field: 'il_location_id_pick_up',
        headerName: 'Pick Up',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        renderCell: (params) => {
            const matchedHBL = bolShipmentList.filter((bol) => bol.il_ftl_ltl_id === params.row.id);
            const result = matchedHBL.map((item) => item.il_location_id_pick_up).join('\n');
            return result ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },
    {
        field: 'il_location_id_deliver_to',
        headerName: 'Delivery',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        renderCell: (params) => {
            const matchedHBL = bolShipmentList.filter((bol) => bol.il_ftl_ltl_id === params.row.id);
            const result = matchedHBL.map((item) => item.il_location_id_deliver_to).join('\n');
            return result ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },
    truckerCol({
        linesByShipmentId: truckerLinesByShipment({ childList: bolShipmentList, childParentKey: 'il_ftl_ltl_id' }),
    }),
    {
        field: 'reference',
        headerName: 'Reference',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-scrollpanel-header',
        renderCell: (params) => {
            const matchedHBL = bolShipmentList.filter((bol) => bol.il_ftl_ltl_id === params.row.id);
            const result = matchedHBL.map((item) => item.reference).join('\n');
            return result ? (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {result.split('\n').map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            ) : (
                ''
            );
        },
    },
    {
        field: 'etd',
        type: 'date',
        pastedValueParser: parsePastedDate,
        headerName: 'ETD',
        width: 70,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        valueGetter: (params) => {
            return parseDateToLocal(params);
        },
        valueFormatter: (params) => {
            if (params) {
                const date = parseDateToLocal(params);
                if (date) {
                    return date.toLocaleDateString('en-US');
                }
            }
            return params;
        },
        renderCell: (params) => {
            if (params.value != null) {
                const date = parseDateToLocal(params.value);
                if (date) {
                    const formattedDate = date.toLocaleDateString('en-US', {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                    });
                    return <Typography sx={customized_style.itemDatagrid_Typography}>{formattedDate}</Typography>;
                }
            }
            return null;
        },
        renderEditCell: (params) => <DateTypingEditCell params={params} />,
    },
    {
        field: 'eta',
        type: 'date',
        pastedValueParser: parsePastedDate,
        headerName: 'ETA',
        width: 70,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        valueGetter: (params) => {
            return parseDateToLocal(params);
        },
        valueFormatter: (params) => {
            if (params) {
                const date = parseDateToLocal(params);
                if (date) {
                    return date.toLocaleDateString('en-US');
                }
            }
            return params;
        },
        renderCell: (params) => {
            if (params.value != null) {
                const date = parseDateToLocal(params.value);
                if (date) {
                    const formattedDate = date.toLocaleDateString('en-US', {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                    });
                    return <Typography sx={customized_style.itemDatagrid_Typography}>{formattedDate}</Typography>;
                }
            }
            return null;
        },
        renderEditCell: (params) => <DateTypingEditCell params={params} />,
    },
    {
        field: 'feta',
        type: 'date',
        pastedValueParser: parsePastedDate,
        headerName: 'F.ETA',
        width: 70,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        valueGetter: (params) => {
            return parseDateToLocal(params);
        },
        valueFormatter: (params) => {
            if (params) {
                const date = parseDateToLocal(params);
                if (date) {
                    return date.toLocaleDateString('en-US');
                }
            }
            return params;
        },
        renderCell: (params) => {
            if (params.value != null) {
                const date = parseDateToLocal(params.value);
                if (date) {
                    const formattedDate = date.toLocaleDateString('en-US', {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                    });
                    return <Typography sx={customized_style.itemDatagrid_Typography}>{formattedDate}</Typography>;
                }
            }
            return null;
        },
        renderEditCell: (params) => <DateTypingEditCell params={params} />,
    },
    ...progressCol({
        tabValue,
        todoSummary,
        commentList,
        width: 110,
    }),
    ...statusCol({
        statusList,
        tabValue,
        width: 250,
    }),
    /** QuickFilter only: `row.__quick_search`는 ShipmentMainTable `shipmentRow`에서 채움 */
    {
        field: '__quick_search',
        headerName: '',
        width: 0,
        minWidth: 0,
        maxWidth: 0,
        sortable: false,
        disableReorder: true,
        getApplyQuickFilterFn: createQuickFilterFn,
        renderCell: () => null,
    },
];
